// ShippingProviderPanel.jsx
//
// Seller-facing shipping provider selector (task spec §8). Mounted at
// the top of the existing Delivery Center page (src/includes/pages/Delivery.jsx,
// route /delivery) rather than a new page/route -- that page is already
// this project's seller shipping/fulfillment hub. Reuses its
// `delivery-panel` / `delivery-btn` / `delivery-select` classes
// (Delivery.css) so this reads as part of the same screen, not a
// bolted-on widget.
//
// The frontend never decides which provider is valid: it only renders
// whatever GET /shipping/providers says is allowed and PUTs the seller's
// choice to the server, which re-validates it (see
// elakay_services/shipping/shippingSettingsService.js).
import React, { useCallback, useEffect, useState } from "react";
import { FiCheckCircle, FiPackage, FiRefreshCw, FiTruck } from "react-icons/fi";
import {
  getShippingProviderStatus,
  updateSellerShippingProvider
} from "../auth/shippingApi";
import { elakayAutoText } from "./dataElakay";

const PROVIDER_META = {
  SHIPPO: {
    icon: <FiTruck />,
    get label() { return elakayAutoText("h_shp_prov_shippo", "Shippo"); },
    get description() {
      return elakayAutoText(
        "h_shp_prov_shippo_desc",
        "Elakay achete et suit vos etiquettes automatiquement via Shippo."
      );
    }
  },
  EASYPOST: {
    icon: <FiPackage />,
    get label() { return elakayAutoText("h_shp_prov_easypost", "EasyPost"); },
    get description() {
      return elakayAutoText(
        "h_shp_prov_easypost_desc",
        "Elakay achete et suit vos etiquettes automatiquement via EasyPost."
      );
    }
  },
  MANUAL: {
    icon: <FiCheckCircle />,
    get label() { return elakayAutoText("h_shp_prov_manual", "Manuel"); },
    get description() {
      return elakayAutoText(
        "h_shp_prov_manual_desc",
        "Vous entrez vous-meme le transporteur et le numero de suivi (page ci-dessous)."
      );
    }
  }
};

function ShippingProviderPanel({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const result = await getShippingProviderStatus(token);
      setStatus(result);
    } catch (err) {
      setError(err.message || elakayAutoText("h_shp_prov_load_err", "Impossible de charger les options d'expedition."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const selectProvider = useCallback(async (provider) => {
    if (!token || saving || provider === status?.sellerProvider) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateSellerShippingProvider(token, provider);
      setStatus((prev) => (prev ? { ...prev, sellerProvider: provider } : prev));
      setMessage(elakayAutoText("h_shp_prov_saved", "Fournisseur d'expedition mis a jour."));
    } catch (err) {
      setError(err.message || elakayAutoText("h_shp_prov_save_err", "Impossible d'enregistrer ce choix."));
    } finally {
      setSaving(false);
    }
  }, [token, saving, status?.sellerProvider]);

  if (!token) return null;

  if (!status && !loading && !error) return null;

  return (
    <section className="delivery-panel" style={{ marginBottom: 16 }}>
      <div className="delivery-panel-head">
        <div>
          <div className="delivery-panel-title">
            {elakayAutoText("h_shp_prov_title", "Fournisseur d'expedition")}
          </div>
          <div className="delivery-panel-subtitle">
            {status?.enabled === false
              ? elakayAutoText("h_shp_prov_globally_off", "L'expedition automatique est actuellement desactivee par Elakay.")
              : elakayAutoText("h_shp_prov_subtitle", "Choisissez comment vos etiquettes d'expedition sont creees.")}
          </div>
        </div>
        <button
          type="button"
          className="delivery-btn delivery-btn-secondary"
          onClick={load}
          disabled={loading}
          aria-label={elakayAutoText("h_shp_prov_refresh", "Actualiser")}
        >
          <FiRefreshCw className={loading ? "delivery-spin" : ""} />
        </button>
      </div>

      {error && <div className="delivery-alert delivery-alert-error" style={{ margin: 14 }}>{error}</div>}
      {message && <div className="delivery-alert delivery-alert-success" style={{ margin: 14 }}>{message}</div>}

      {status && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, padding: 14 }}>
          {["SHIPPO", "EASYPOST", "MANUAL"].map((provider) => {
            const allowed = status.allowedProviders?.includes(provider);
            const active = status.sellerProvider === provider;
            const meta = PROVIDER_META[provider];
            return (
              <button
                type="button"
                key={provider}
                disabled={!allowed || saving}
                onClick={() => selectProvider(provider)}
                className={`delivery-btn ${active ? "delivery-btn-primary" : "delivery-btn-secondary"}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 6,
                  padding: "12px 14px",
                  opacity: allowed ? 1 : 0.45,
                  cursor: allowed ? "pointer" : "not-allowed",
                  textAlign: "left",
                  height: "auto"
                }}
                title={!allowed ? elakayAutoText("h_shp_prov_not_allowed", "Non disponible -- desactive par Elakay ou non configure.") : ""}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                  {meta.icon}{meta.label}{active && <FiCheckCircle />}
                </span>
                <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.85 }}>{meta.description}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default ShippingProviderPanel;
